/**
 * The two moving space details.
 *
 * Both are decoration. Neither reports a status, and removing either loses
 * nothing: the loading text says the operation is pending, and the tracker's
 * stage list says the job finished.
 *
 * - `LoadingMark` turns slowly while something is genuinely pending. It is a
 *   functional spinner, so it may repeat — but only because it is unmounted
 *   the instant the loading state ends. Nothing keeps it alive.
 * - `LaunchMark` lifts once when a job is **confirmed** to have finished. It
 *   fires on a real transition and on nothing else: not on mount, not on a
 *   refresh, not on a poll that returns the same answer, not on a rerender,
 *   and not when a tab is brought back.
 *
 * The preference chain in `illustrations.tsx` decides both: the organization
 * may switch artwork off, the person may choose Static or Off, and the
 * operating system's reduced-motion request overrides everything and cannot
 * be overridden by a user choice.
 *
 * See `docs/SPACE_DETAILS.md`.
 */

import { useRouterState } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";

import { classes } from "../primitives.tsx";
import { Icon } from "../icon.tsx";
import { useIllustrations } from "./illustrations.tsx";

/**
 * The screens that keep the plain presentation.
 *
 * Authentication, account recovery, security, the assessor portal, the
 * employee portal, and the public trust page carry no decoration at all —
 * not a still mark and not a moving one. `docs/SPACE_DETAILS.md` places them
 * outside decorative presentation, and this is the one list that enforces it,
 * so no individual page has to remember.
 */
const PLAIN_PREFIXES: readonly string[] = [
  "/security",
  "/audit/accept",
  "/audit/portal",
  "/employee",
  "/public-page",
  "/organizations/new",
];

function onPlainScreen(pathname: string): boolean {
  return PLAIN_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

/**
 * A slow-turning astronaut beside a pending operation.
 *
 * With artwork off there is no mark at all. With motion off the astronaut is
 * still there and simply does not turn, which is the required still
 * alternative rather than an empty space.
 */
export function LoadingMark({ className }: { readonly className?: string }) {
  const illustrations = useIllustrations();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  if (!illustrations.show || onPlainScreen(pathname)) {
    return null;
  }
  return (
    <Icon
      name="astronaut"
      size="1.1em"
      className={
        (illustrations.motion ? "arc-orbit " : "") + (className ?? "text-muted")
      }
    />
  );
}

/**
 * Report a confirmed change from not-done to done.
 *
 * `null` until the first answer is seen, so the state a page loads with is
 * never treated as something that just happened. A refreshed page shows the
 * current state; it does not replay a launch.
 *
 * Going backwards — a reopened job — clears the mark and arms it again,
 * without playing anything.
 */
function useConfirmedCompletion(done: boolean, motion: boolean): boolean {
  const seen = useRef<boolean | null>(null);
  const [launching, setLaunching] = useState(false);

  useEffect(() => {
    const previous = seen.current;
    seen.current = done;

    // The first answer is the current state, not an event.
    if (previous === null) return;
    // A poll that returns the same answer, or a rerender. Nothing happened.
    if (previous === done) return;
    // Reopened. Arm again, play nothing.
    if (!done) return;
    if (!motion) return;
    // Do not spend the one play on a tab nobody is looking at.
    if (typeof document !== "undefined" && document.visibilityState !== "visible") {
      return;
    }

    setLaunching(true);
    const timer = window.setTimeout(() => setLaunching(false), 900);
    return () => window.clearTimeout(timer);
  }, [done, motion]);

  return launching;
}

/**
 * A rocket that lifts once when a job is confirmed finished.
 *
 * `done` must come from the job's own recorded state. A tracker that cannot
 * say whether the work finished passes `false`; it never guesses, and a
 * failed, cancelled, or stale job is not done.
 */
export function LaunchMark({
  done,
  className,
}: {
  readonly done: boolean;
  readonly className?: string;
}) {
  const illustrations = useIllustrations();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const launching = useConfirmedCompletion(done, illustrations.motion);

  if (!illustrations.show || !done || onPlainScreen(pathname)) {
    return null;
  }
  return (
    <Icon
      name="rocket"
      size="1.25em"
      className={(launching ? "arc-launch " : "") + (className ?? "text-muted")}
    />
  );
}

/**
 * An astronaut on a moon buggy, above the waiting bar.
 *
 * It holds its place and the ground travels under it. That is what makes it
 * read as driving, and it is also the honest shape: the buggy marks no
 * position on the bar, because the thing being waited for cannot report one.
 * See the note on `Waiting` in the audit procedure. A bar that invents a
 * percentage is a lie about a wait, and so is a buggy parked at the 60% mark.
 *
 * With artwork off there is nothing. With motion off the buggy and the
 * ground are both still, which is the same picture rather than a gap.
 */
export function BuggyProgress({ motion }: { readonly motion: boolean }) {
  const illustrations = useIllustrations();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  if (!illustrations.show || onPlainScreen(pathname)) {
    return null;
  }
  const rolling = motion && illustrations.motion;
  return (
    // Its own lane, in normal flow, above the bar. `overflow-hidden` keeps
    // the scene inside it, so nothing can reach the bar and nothing can
    // widen the page — which means the lane has to be taller than the
    // artwork by more than the bounce travels, or it clips his helmet at
    // the top of every bump. Stage 42.4px, lift 1.6px, lane 48px.
    <div
      className="pointer-events-none relative mb-1.5 h-12 w-full overflow-hidden"
      aria-hidden="true"
      data-testid="audit-waiting-mark"
    >
      {/* The ground travels; the buggy holds its place on the screen. */}
      <div
        className={classes(
          "absolute inset-x-0 bottom-0 h-4 opacity-60",
          rolling && "arc-moonscape",
        )}
        style={rolling ? undefined : MOONSCAPE_STILL}
      />
      <div className="absolute inset-x-0 bottom-0 h-px bg-divider" />
      <div className="absolute bottom-px left-[18%]">
        <MoonBuggy rolling={rolling} />
      </div>
    </div>
  );
}

/**
 * An astronaut on a moon buggy.
 *
 * The astronaut is Font Awesome artwork. The buggy is not — there is no
 * buggy in the icon set, and its wheels have to turn independently of the
 * body, which is exactly what the CSS-shape allowance is for. It is four
 * boxes and two circles, built to read at about 30 pixels wide.
 *
 * Nested so the transforms do not collide: the body rides the bumps, and
 * each wheel turns inside its own mount.
 */
function MoonBuggy({ rolling }: { readonly rolling: boolean }) {
  return (
    <div style={{ position: "relative", width: STAGE.w, height: STAGE.h }}>
      <Part src="/moon-buggy-body.png" box={BODY} moving={rolling} className="arc-ride" />
      <Part src="/moon-buggy-wheel-rear.png" box={REAR} moving={rolling} className="arc-bump" />
      <Part src="/moon-buggy-wheel-front.png" box={FRONT} moving={rolling} className="arc-bump" />
    </div>
  );
}

/**
 * One piece of the drawing.
 *
 * The artwork is used as a **mask**, not as an image: the file carries the
 * shape in its alpha channel and the colour comes from `currentColor`. A
 * line drawing painted straight onto the page would be stuck at whatever
 * colour it was exported in, would not match the muted grey beside it, and
 * would be wrong the day a dark theme arrives.
 */
function Part({
  src,
  box,
  moving,
  className,
}: {
  readonly src: string;
  readonly box: { readonly x: number; readonly y: number; readonly w: number; readonly h: number };
  readonly moving: boolean;
  readonly className: string;
}) {
  return (
    <div
      className={classes("absolute", moving && className)}
      style={{
        left: box.x, top: box.y, width: box.w, height: box.h,
        backgroundColor: "currentColor",
        maskImage: `url(${src})`,
        WebkitMaskImage: `url(${src})`,
        maskSize: "100% 100%",
        WebkitMaskSize: "100% 100%",
        maskRepeat: "no-repeat",
        WebkitMaskRepeat: "no-repeat",
      }}
    />
  );
}

/**
 * Where each piece sits.
 *
 * The sprites are cut from one drawing at three times this size, so the
 * offsets are that drawing's own coordinates divided by three. Keeping them
 * here, derived from one scale, is what stops the wheels drifting away from
 * the arches when anything is resized.
 */
const SPRITE_SCALE = 2.5;
const px = (value: number): number => Math.round((value / SPRITE_SCALE) * 100) / 100;
const STAGE = { w: px(132), h: px(106) };
const BODY = { x: 0, y: 0, w: STAGE.w, h: STAGE.h };
const REAR = { x: px(3.26), y: px(78.06), w: px(29), h: px(29) };
const FRONT = { x: px(96.31), y: px(78.06), w: px(29), h: px(29) };

/**
 * The same ground, not moving.
 *
 * With motion off the strip keeps its hills and craters and simply stays
 * where it is, so the still alternative is the same picture rather than an
 * empty band.
 */
const MOONSCAPE_STILL: React.CSSProperties = {
  backgroundRepeat: "repeat-x",
  backgroundSize: "140px 100%",
  backgroundImage: [
    "radial-gradient(circle 1.5px at 34px calc(100% - 3px), var(--arc-divider) 0 100%, transparent 0)",
    "radial-gradient(circle 1.5px at 88px calc(100% - 2px), var(--arc-divider) 0 100%, transparent 0)",
    "radial-gradient(ellipse 13px 6px at 16px 100%, var(--arc-divider) 0 70%, transparent 71%)",
    "radial-gradient(ellipse 22px 9px at 62px 100%, var(--arc-divider) 0 70%, transparent 71%)",
    "radial-gradient(ellipse 16px 5px at 112px 100%, var(--arc-divider) 0 70%, transparent 71%)",
  ].join(", "),
};
